import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Loader } from "@/components/ui/Loader";
import { Select } from "@/components/ui/Select";
import {
  AVALIACAO_OPCOES,
  CLAREZA_OPCOES,
  CONDICOES_OPCOES,
  CRESCIMENTO_OPCOES,
  FREQUENCIA_OPCOES,
  MOTIVO_OPCOES,
  enviarDesligamento,
  extractDesligamentoError,
  fetchDesligamentoPublico,
  type DesligamentoPublica,
} from "@/services/desligamento";

const VAZIO = {
  motivo: "",
  motivoOutro: "",
  influencia: "",
  lideranca: "",
  liderancaPorque: "",
  respeito: "",
  condicoes: "",
  condicoesFaltava: "",
  clareza: "",
  crescimento: "",
  crescimentoComente: "",
  problema: "",
  problemaDescricao: "",
  permanecer: "",
  manter: "",
  melhorar: "",
  observacoes: "",
};

function CampoTexto({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <textarea
        className="min-h-24 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export function DesligamentoPublicaPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [meta, setMeta] = useState<DesligamentoPublica | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState(VAZIO);

  function set<K extends keyof typeof VAZIO>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    let cancel = false;
    fetchDesligamentoPublico(token)
      .then((res) => {
        if (!cancel) setMeta(res);
      })
      .catch((e) => {
        if (!cancel) setErro(extractDesligamentoError(e, "Este convite não está disponível."));
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await enviarDesligamento(token, form);
      setOk(true);
    } catch (err) {
      setErro(extractDesligamentoError(err, "Não foi possível enviar a entrevista."));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain bg-slate-100 text-slate-800">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center justify-center px-4 py-3">
          <span className="text-sm font-bold tracking-wide text-brand-700">SEMOG · Entrevista de desligamento</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-xl px-4 py-6 pb-28">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader label="Carregando entrevista..." />
          </div>
        ) : erro && !meta ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-card">
            <h1 className="text-xl font-bold">Convite indisponível</h1>
            <p className="mt-3 text-sm text-slate-600">{erro}</p>
          </div>
        ) : ok || meta?.estado === "respondida" ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-card">
            <h1 className="text-xl font-bold">Obrigado</h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Sua entrevista foi registrada. Obrigado por contribuir para que a SEMOG seja, a cada dia, um lugar melhor para trabalhar.
            </p>
          </div>
        ) : meta?.estado === "encerrada" ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-card">
            <h1 className="text-xl font-bold">Prazo encerrado</h1>
            <p className="mt-3 text-sm text-slate-600">O prazo para responder esta entrevista já terminou.</p>
          </div>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
            <div>
              <h1 className="text-xl font-bold">Entrevista de desligamento</h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Gestão de Pessoas. O objetivo é compreender, com respeito e confidencialidade, os fatores que influenciaram o desligamento.
                A participação é voluntária.
              </p>
              <p className="mt-2 text-sm text-slate-700">
                {meta?.nome}
                {meta?.cargo ? ` · ${meta.cargo}` : ""}
                {meta?.setor ? ` · ${meta.setor}` : ""}
              </p>
            </div>
            {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}
            <Select label="1. Qual é o principal motivo do seu desligamento?" value={form.motivo} options={MOTIVO_OPCOES} onChange={(e) => set("motivo", e.target.value)} required />
            {form.motivo === "OUTRO" && <Input label="Qual outro motivo?" value={form.motivoOutro} onChange={(e) => set("motivoOutro", e.target.value)} required />}
            <CampoTexto label="2. O que mais influenciou sua decisão de sair da SEMOG?" value={form.influencia} onChange={(v) => set("influencia", v)} />
            <Select label="3. Como você avalia o relacionamento com sua liderança direta?" value={form.lideranca} options={AVALIACAO_OPCOES} onChange={(e) => set("lideranca", e.target.value)} required />
            <CampoTexto label="Por quê?" value={form.liderancaPorque} onChange={(v) => set("liderancaPorque", v)} hint="Opcional" />
            <Select label="4. Você se sentia respeitado(a), ouvido(a) e valorizado(a) no trabalho?" value={form.respeito} options={FREQUENCIA_OPCOES} onChange={(e) => set("respeito", e.target.value)} required />
            <Select label="5. Como eram as condições de trabalho (estrutura, materiais, segurança e organização)?" value={form.condicoes} options={CONDICOES_OPCOES} onChange={(e) => set("condicoes", e.target.value)} required />
            <CampoTexto label="O que faltava?" value={form.condicoesFaltava} onChange={(v) => set("condicoesFaltava", v)} hint="Opcional" />
            <Select label="6. Suas atividades, responsabilidades e expectativas eram comunicadas com clareza?" value={form.clareza} options={CLAREZA_OPCOES} onChange={(e) => set("clareza", e.target.value)} required />
            <Select label="7. Você percebia oportunidades de desenvolvimento e crescimento na empresa?" value={form.crescimento} options={CRESCIMENTO_OPCOES} onChange={(e) => set("crescimento", e.target.value)} required />
            <CampoTexto label="Comente" value={form.crescimentoComente} onChange={(v) => set("crescimentoComente", v)} hint="Opcional" />
            <Select
              label="8. Houve alguma situação ou problema que você comunicou e não foi resolvido?"
              value={form.problema}
              options={[
                { value: "NAO", label: "Não" },
                { value: "SIM", label: "Sim" },
              ]}
              onChange={(e) => set("problema", e.target.value)}
              required
            />
            {form.problema === "SIM" && (
              <CampoTexto label="Se desejar, descreva" value={form.problemaDescricao} onChange={(v) => set("problemaDescricao", v)} hint="Opcional" />
            )}
            <CampoTexto label="9. O que a SEMOG poderia ter feito para que você considerasse permanecer?" value={form.permanecer} onChange={(v) => set("permanecer", v)} />
            <CampoTexto label="10. O que a SEMOG deve manter?" value={form.manter} onChange={(v) => set("manter", v)} />
            <CampoTexto label="O que a SEMOG precisa melhorar?" value={form.melhorar} onChange={(v) => set("melhorar", v)} />
            <CampoTexto label="Observações adicionais" value={form.observacoes} onChange={(v) => set("observacoes", v)} hint="Opcional" />
            <Button type="submit" loading={enviando}>
              Enviar entrevista
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
